import React, { useState, useRef, useEffect } from "react";
import { InformationCircleIcon, XIcon } from "@heroicons/react/outline";

const COPY = {
	guided: {
		title: "Guided",
		desc:
			"Join a small group led by an expert local guide. Your guide handles navigation, safety, cultural insights, and daily logistics — all while a support vehicle follows in the background to make your trip smooth and worry-free.",
	},
	selfguided: {
		title: "Self-Guided",
		desc:
			"Ride or hike independently with app-based navigation, luggage transfers, and local phone support. You manage your own navigation and basic bike maintenance.",
	},
	vansupported: {
		title: "Van-Supported",
		desc:
			"Our support van handles luggage transfers and provides on-route assistance or a lift when needed. You manage your own navigation and basic bike maintenance; no local guide accompanies you.",
	},
};

export default function TourTypeBadge({ type }) {
	const key = (type || "").toString().trim().toLowerCase();
	const cfg = COPY[key];
	if (!cfg) return null;

	const [open, setOpen] = useState(false);
	const btnRef = useRef(null);
	const popRef = useRef(null);

	useEffect(() => {
		function onDocClick(e) {
			if (!open) return;
			const inPanel = popRef.current?.contains(e.target);
			const inBtn = btnRef.current?.contains(e.target);
			if (!inPanel && !inBtn) setOpen(false);
		}
		function onEsc(e) {
			if (e.key === "Escape") setOpen(false);
		}
		document.addEventListener("mousedown", onDocClick);
		document.addEventListener("keydown", onEsc);
		return () => {
			document.removeEventListener("mousedown", onDocClick);
			document.removeEventListener("keydown", onEsc);
		};
	}, [open]);

	return (
		<span className="relative inline-flex">
			{/* Info Icon trigger */}
			<button
				ref={btnRef}
				type="button"
				aria-haspopup="dialog"
				aria-expanded={open}
				aria-label={`${cfg.title} tour info`}
				onClick={() => setOpen((v) => !v)}
				onKeyDown={(e) => {
					if (e.key === " " || e.key === "Enter") {
						e.preventDefault();
						setOpen((v) => !v);
					}
				}}
				className="-mt-0.5 inline-flex focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
			>
				<InformationCircleIcon className="h-4 w-4 text-primary" />
			</button>

			{open && (
				<div
					ref={popRef}
					role="dialog"
					tabIndex={-1}
					className="absolute z-50 w-72 max-w-[80vw] rounded-xl border border-gray-200 bg-white shadow-lg p-4 text-sm focus:outline-none top-1/2 left-1/2 transform -translate-x-1/3 -translate-y-1/2"
				>
					<button
						type="button"
						onClick={() => setOpen(false)}
						aria-label="Close"
						className="absolute top-2 right-2 text-gray-400 hover:text-gray-600 focus:outline-none"
					>
						<XIcon className="h-4 w-4" />
					</button>

					<div className="font-semibold text-gray-900 mb-1">{cfg.title} Tour</div>
					<p className="text-sm text-gray-500 lowercase">{cfg.desc}</p>
				</div>
			)}
		</span>
	);
}
